class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    // Boost instant search owns the dropdown when it is active, so skip the native requests
    if (document.querySelector('.boost-sd__search-widget-init-enabled')) return;

    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.fetchResultsMarkup(searchTerm)
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  // The Predictive Search API answers 417 "Unsupported buyer locale" for some storefront languages (Thai),
  // so after the first failure we ask the search page to render the same section instead
  fetchResultsMarkup(searchTerm) {
    const term = encodeURIComponent(searchTerm);
    const options = { signal: this.abortController.signal };
    const fallbackUrl = `${routes.search_url}?q=${term}&section_id=predictive-search&type=product&options[prefix]=last`;
    const predictiveUrl = `${routes.predictive_search_url}?q=${term}&section_id=predictive-search&resources[type]=query,product,collection&resources[limit]=10&resources[options][unavailable_products]=last`;

    const request = (url) =>
      fetch(url, options).then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          error.status = response.status;
          throw error;
        }
        return response.text();
      });

    if (PredictiveSearch.useSearchPageFallback) return request(fallbackUrl);

    return request(predictiveUrl).catch((error) => {
      if (error.status !== 417) throw error;
      PredictiveSearch.useSearchPageFallback = true;
      return request(fallbackUrl);
    });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  // Mobile: the dropdown is a fixed panel that starts right under the search bar
  updatePanelTop() {
    const bar = this.closest('.search-modal__content');
    if (!bar) return;
    document.documentElement.style.setProperty('--ps-top', `${Math.round(bar.getBoundingClientRect().bottom)}px`);
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.updatePanelTop();
    setTimeout(() => this.updatePanelTop(), 450);
    this.panelTopListener = this.panelTopListener || this.updatePanelTop.bind(this);
    window.addEventListener('resize', this.panelTopListener);
    window.addEventListener('scroll', this.panelTopListener, { passive: true });
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    if (this.panelTopListener) {
      window.removeEventListener('resize', this.panelTopListener);
      window.removeEventListener('scroll', this.panelTopListener);
    }
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

class PredictiveSearchCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-predictive-search-track]');
    this.prev = this.querySelector('.predictive-search__nav--prev');
    this.next = this.querySelector('.predictive-search__nav--next');
    this.dots = this.querySelector('[data-predictive-search-dots]');
    if (!this.track) return;

    this.prev.addEventListener('click', () => this.scrollByPage(-1));
    this.next.addEventListener('click', () => this.scrollByPage(1));
    // Keep the search input focused so the dropdown does not close
    [this.prev, this.next].forEach((button) => button.addEventListener('mousedown', (event) => event.preventDefault()));
    this.track.addEventListener('scroll', () => this.update(), { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.buildDots());
    this.resizeObserver.observe(this.track);
    this.buildDots();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  get maxScroll() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  get pageCount() {
    return this.track.clientWidth ? Math.max(1, Math.ceil(this.track.scrollWidth / this.track.clientWidth)) : 1;
  }

  scrollByPage(direction) {
    this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: 'smooth' });
  }

  // Keep the arrows centred on the card images, whatever the card width or image ratio
  positionNav() {
    const image = this.track.querySelector('.card__inner');
    if (!image) return;
    const imageRect = image.getBoundingClientRect();
    if (!imageRect.height) return;
    const center = imageRect.top - this.getBoundingClientRect().top + imageRect.height / 2;
    const navHeight = parseFloat(getComputedStyle(this.next).height) || 0;
    this.style.setProperty('--ps-nav-top', `${center - navHeight / 2}px`);
  }

  buildDots() {
    this.positionNav();
    const count = this.pageCount;
    this.dots.hidden = count < 2;
    this.dots.innerHTML = '<span></span>'.repeat(count);
    this.update();
  }

  update() {
    const max = this.maxScroll;
    this.prev.hidden = this.track.scrollLeft <= 1;
    this.next.hidden = this.track.scrollLeft >= max - 1;
    const dots = this.dots.children;
    if (!dots.length) return;
    const active = max > 0 ? Math.round((this.track.scrollLeft / max) * (dots.length - 1)) : 0;
    Array.from(dots).forEach((dot, index) => dot.classList.toggle('is-active', index === active));
  }
}

customElements.define('predictive-search-carousel', PredictiveSearchCarousel);
